import { useEffect, useRef, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Terminal, Trash2, Download, Activity, Bug, ArrowRight, Radar } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

const COLOR = {
  info: "text-muted-foreground",
  cmd: "text-foreground",
  ok: "text-success",
  warn: "text-warning",
  crit: "text-primary",
  err: "text-destructive",
};

export default function LiveTerminal() {
  const [params] = useSearchParams();
  const scanId = params.get("scan");
  const [scan, setScan] = useState(null);
  const [recent, setRecent] = useState([]);
  const scrollRef = useRef(null);

  const running = scan?.status === "running";

  const poll = useCallback(async () => {
    if (!scanId) return;
    try {
      const data = await api.getScan(scanId);
      setScan(data);
      return data.status;
    } catch { return "error"; }
  }, [scanId]);

  useEffect(() => {
    if (!scanId) { api.listScans().then(setRecent).catch(() => {}); return; }
    let timer;
    const loop = async () => {
      const status = await poll();
      if (status === "running") timer = setTimeout(loop, 1200);
    };
    loop();
    return () => clearTimeout(timer);
  }, [scanId, poll]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [scan?.lines?.length]);

  const lines = scan?.lines || [];
  const stats = {
    total: lines.length,
    warn: lines.filter(l => l.type === "warn").length,
    crit: lines.filter(l => l.type === "crit").length,
  };

  const exportLog = () => {
    const txt = lines.map(l => `${l.ts}  ${l.text}`).join("\n");
    const blob = new Blob([txt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `scan-${scanId}.log`; a.click();
    URL.revokeObjectURL(url);
  };

  // No scan selected -> picker
  if (!scanId) {
    return (
      <div className="px-4 md:px-8 py-8 max-w-[1600px] mx-auto space-y-4 animate-fade-in" data-testid="terminal-page">
        <div>
          <div className="font-mono text-xs text-primary tracking-widest mb-2">// MODULE 03 · LIVE OPS TERMINAL</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><Terminal className="h-8 w-8 text-primary"/> Real-time scan stream</h1>
          <p className="text-muted-foreground mt-1 text-sm">Sélectionne un scan récent ou lance-en un nouveau.</p>
        </div>
        <Card className="bg-card/60 border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold">Scans récents</h3>
            <Link to="/scanner"><Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs gap-2"><Radar className="h-3.5 w-3.5"/>NOUVEAU SCAN</Button></Link>
          </div>
          <div className="divide-y divide-border">
            {recent.length === 0 && <div className="p-8 text-center text-muted-foreground font-mono text-sm">// aucun scan pour l'instant</div>}
            {recent.map(s => (
              <Link key={s.id} to={`/terminal?scan=${s.id}`} data-testid={`recent-scan-${s.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-muted/20 transition-colors">
                <div className="min-w-0">
                  <div className="font-mono text-sm text-foreground">{s.target}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{s.profile} · {new Date(s.started_at).toLocaleString()}</div>
                </div>
                <Badge variant="outline" className={cn("font-mono text-[10px]", s.status === "running" ? "border-success/40 text-success" : s.status === "failed" ? "border-destructive/40 text-destructive" : "border-border text-muted-foreground")}>{s.status}</Badge>
              </Link>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-8 max-w-[1600px] mx-auto space-y-4 animate-fade-in" data-testid="terminal-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-primary tracking-widest mb-2">// MODULE 03 · LIVE OPS TERMINAL</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><Terminal className="h-8 w-8 text-primary"/> {scan?.target || "scan"}</h1>
          <p className="text-muted-foreground mt-1 text-sm font-mono">{scan?.command}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportLog} className="gap-2 font-mono text-xs"><Download className="h-3.5 w-3.5"/>EXPORT</Button>
          <Link to="/terminal"><Button variant="outline" size="sm" className="gap-2 font-mono text-xs"><Trash2 className="h-3.5 w-3.5"/>SCANS</Button></Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        <Card className="bg-card/60 border-border p-4">
          <div className="font-mono text-[10px] tracking-widest text-muted-foreground">STATUS</div>
          <div className="flex items-center gap-2 mt-2">
            <span className={cn("h-2 w-2 rounded-full", running ? "bg-success pulse-dot" : scan?.status === "failed" ? "bg-destructive" : "bg-muted-foreground")}/>
            <span data-testid="scan-status" className={cn("font-mono text-sm font-bold", running ? "text-success" : scan?.status === "failed" ? "text-destructive" : "text-muted-foreground")}>{(scan?.status || "…").toUpperCase()}</span>
          </div>
        </Card>
        <Card className="bg-card/60 border-border p-4"><div className="font-mono text-[10px] tracking-widest text-muted-foreground">LINES</div><div className="font-mono text-2xl font-bold mt-2">{stats.total}</div></Card>
        <Card className="bg-card/60 border-border p-4"><div className="font-mono text-[10px] tracking-widest text-muted-foreground">HOSTS UP</div><div className="font-mono text-2xl font-bold text-info mt-2">{scan?.hosts?.length || 0}</div></Card>
        <Card className="bg-card/60 border-border p-4"><div className="font-mono text-[10px] tracking-widest text-muted-foreground">VULNS</div><div className="font-mono text-2xl font-bold text-primary mt-2">{scan?.vulns?.length || 0}</div></Card>
      </div>

      <Card className="bg-terminal border-border overflow-hidden shadow-red-glow">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-card/60">
          <span className="h-2.5 w-2.5 rounded-full bg-primary/70"/>
          <span className="h-2.5 w-2.5 rounded-full bg-warning/70"/>
          <span className="h-2.5 w-2.5 rounded-full bg-success/70"/>
          <span className="ml-3 font-mono text-[11px] text-muted-foreground">root@crimson — {scan?.target} — nmap</span>
          <div className="ml-auto">
            <Badge variant="outline" className="font-mono text-[10px] border-success/40 text-success gap-1.5"><Activity className="h-3 w-3"/>{running ? "LIVE" : "DONE"}</Badge>
          </div>
        </div>
        <div ref={scrollRef} data-testid="terminal-output" className="scanline p-5 font-mono text-[12.5px] leading-relaxed h-[520px] overflow-y-auto">
          {lines.map((l, i) => (
            <div key={i} className="flex gap-3 animate-slide-in">
              <span className="text-muted-foreground/60 shrink-0">{(l.ts || "").slice(11, 19)}</span>
              <span className={cn("flex-1 break-all", COLOR[l.type])}>{l.text}</span>
            </div>
          ))}
          {running && <div className="flex gap-3"><span className="text-primary terminal-caret">❯ </span></div>}
        </div>
      </Card>

      {!running && (scan?.vulns?.length > 0) && (
        <Card className="bg-card/60 border-primary/30 p-5 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center shrink-0"><Bug className="h-5 w-5 text-primary"/></div>
            <div>
              <div className="font-mono text-[10px] tracking-widest text-primary mb-1">// SCAN TERMINÉ</div>
              <div className="font-semibold">{scan.vulns.length} vulnérabilité(s) détectée(s)</div>
              <p className="text-sm text-muted-foreground mt-0.5">Consulte le détail et les recommandations de correction.</p>
            </div>
          </div>
          <Link to="/vulnerabilities"><Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs gap-2 shadow-red-glow">VOIR LES VULNÉRABILITÉS <ArrowRight className="h-3.5 w-3.5"/></Button></Link>
        </Card>
      )}
    </div>
  );
}
